import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {Col, Container, Row} from 'reactstrap';
import classNames from 'classnames';
import {DateTime} from 'luxon';
import {Link, animateScroll as scroll} from 'react-scroll';
import PageMeta from 'components/PageMeta';
import ABTest, {DebugMenu} from 'react-controlled-ab';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState, usePhaseDispatch} from 'funnel-schedule/phase-context';
import {useUserState, useUserDispatch} from 'contexts/user';
import useBalanceText from 'utils/useBalanceText';
import track from 'utils/track';

import {SectionTriangle} from 'components/Elements/Section';
import Laurels from 'components/Laurels';
import Webinar from 'components/Webinar';
import CountdownTimer from 'components/Elements/CountdownTimer';
import SalesCopy from 'components/SalesCopy';
import CheckoutButton from 'components/CheckoutButton';
import {SalesDisclaimer} from 'components/SalesCta';
import ModalExitScreening from 'components/Modals/ModalExitScreening';
import RegistrationModal from 'components/Modals/RegistrationModal';
import ShareButtons from 'components/Social/ShareButtons';
import Footer from 'components/Elements/Footer';

//Due to a limitation of dotenv we must use three seperate statements
const {SF_API_URL} = process.env;
const {SITE_ID} = process.env;

export default function Screening({screeningFull = false, ...props}) {
  const phaseDispatch = usePhaseDispatch();
  const userDispatch = useUserDispatch();
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: '',
        launchTitle: ''
      },
      sales: salesSchedule = {
        phase: 'during',
        salesEnd: '',
        isSalesDay: false
      },
      webinar: webinarSchedule = {
        phase: 'before',
        time: 1
      }
    }
  } = usePhaseState();

  const {events = [], registrationId} = useUserState();
  const videoWatched =
    events.includes('Watched FF Screening') || (typeof window !== 'undefined' && window.location.hash === '#impact');
  const [anchorScrolled, setAnchorScrolled] = useState(false);

  useEffect(() => {
    registrationId &&
      axios
        .get(`${SF_API_URL}/site/${SITE_ID}/state/`, {
          params: {
            registrationId
          }
        })
        .then(({data}) => {
          if (!data.webinar) {
            data.webinar = {
              phase: 'after',
              time: Math.floor(DateTime.utc().minus({days: 5}).toSeconds())
            };
          }
          const {firedEvents = []} = data.webinar;

          phaseDispatch({
            type: 'SET_PHASE',
            payload: data
          });
          firedEvents.map((e) => userDispatch({type: 'ADD_EVENT', payload: e}));
        });
  }, []);

  useEffect(() => {
    if (anchorScrolled) return;
    if (events.includes('Watched FF Screening')) {
      setAnchorScrolled(true);
      if (window !== undefined && window.location.hash === '#impact') {
        document.getElementById('impact-cta').scrollIntoView();
      }
    }
  }, [events, anchorScrolled]);

  const productID = 'ff';
  const productSku = 'ff';
  const productName = 'Fantastic Fungi Impact Kit';
  const productSlug = 'ff-impactkit-special';
  const productValue = 67;
  const productDvdName = 'Fantastic Fungi Film';
  const productDvdSlug = 'ff-special';
  const productDvdValue = 7;
  const bonusValue = '500';

  useEffect(
    () =>
      track('Product Viewed', {
        product_id: productID,
        sku: productSku,
        name: productName,
        variant: productSlug,
        value: productValue,
        currency: 'usd'
      }),
    []
  );
  useEffect(
    () =>
      track('Product Viewed', {
        product_id: productID,
        sku: productSku,
        name: productDvdName,
        variant: productDvdName,
        value: productDvdValue,
        currency: 'usd'
      }),
    []
  );

  function ScreeningCheckoutButton({className = null, ...props}) {
    return (
      <CheckoutButton color="cta-ff" sku={productSku} slug={productSlug} price={productValue} className={className}>
        Buy Now
      </CheckoutButton>
    );
  }

  function Timer({className = null, ...props}) {
    return (
      <>
        <div className={classNames('section-countdown text-center mt-4', className)}>
          <Row className="d-flex align-items-center justify-content-center m-0">
            <Col xs="12">
              <h5 className="section-heading text-ff-orange mb-3">
                The special event free viewing period will end in:
              </h5>
            </Col>
            <Col xs="12" className="timer countdown-dark">
              <CountdownTimer time={webinarSchedule.time * 1000 + 345600000} />
            </Col>
          </Row>
        </div>
      </>
    );
  }

  /* const webinarHeading = (
    <Row>
      <Col>
        <h3 className="section-heading mb-3">
          {videoWatched ? (
            <>
              Scroll down to learn more about the <i>Fantastic Fungi Impact Kit</i>
            </>
          ) : (
            <>
              Welcome to the Special Event Edition of <i>Fantastic Fungi</i>
            </>
          )}
        </h3>
        <p className="mb-4">4-Day Screening Event</p>
      </Col>
    </Row>
  );*/

  const webinarHeading = (
    <Row>
      <Col>
        <h3 className="section-heading mb-3">
          Welcome to the Special Event Edition of <i>Fantastic Fungi</i>
        </h3>
        <p className="mb-4">4-Day Screening Event</p>
      </Col>
    </Row>
  );

  const webinarFooter = <>{webinarSchedule.phase === 'after' ? null : <Timer />}</>;

  useBalanceText();
  return (
    <div className="page d-flex flex-column">
      <PageMeta />

      <div className="page-content">
        <SectionTriangle
          id="ff-screening-header-1"
          color="black"
          className="section-triangle-first"
          imgUrl={require('static/backgrounds/ff-saleslanding-image-header-backdrop-landon-parenteau-EEuDMqRYbx0-unsplash.jpg')}
          innerClass="p-0"
        />

        <SectionTriangle id="ff-screening-header-2" color="white" type="top">
          <Container>
            <div className="offset-box background-white-80 border-ff-gray-10-solid text-center p-3 p-md-4 p-lg-5">
              <Laurels className="mb-5" />
              <div className="offset-box-webinar">
                <Webinar
                  headingSection={webinarHeading}
                  footerSection={webinarFooter}
                  noun="film"
                  label="FF - Film"
                  webinarVideoCopy="Click here to watch the film"
                  controls={true}
                  poster="https://cdn.foodrevolution.org/ff/ff-film-thumbnail.jpg"
                />
              </div>
              <Row className="offset-box-cta">
                <Col>
                  <h3 className="section-heading mb-2">
                    Own the Special Event Edition of <i>Fantastic Fungi</i> for Life
                  </h3>
                  <h2 className="mt-0 mb-3">Right now just $7</h2>
                  <p className="text-ff-muted-orange text-accent">(Less than the cost of a movie ticket!)</p>
                  <CheckoutButton color="cta-ff" sku={productSku} slug={productDvdSlug} price={productDvdValue}>
                    Buy Now
                  </CheckoutButton>
                  <SalesDisclaimer />
                </Col>
              </Row>
            </div>
          </Container>
        </SectionTriangle>

        <SalesCopy version="screening">
          <div className="cta-box border-ff-orange-5-solid text-center">
            <SectionTriangle id="" color="ff-cream" type="bottom" innerClass="pt-4 pt-lg-5">
              <h4 className="section-heading">
                By taking action NOW, you’ll receive over 65% off the full price of $197
              </h4>
              <h5 className="m-0">
                Regular Price: <del>$197</del>
              </h5>
            </SectionTriangle>
            <SectionTriangle id="" color="white" type="bottom" className="text-ff-orange">
              <h3 className="section-heading mb-2">Special Price Only On This Page:</h3>
              <h2 className="text-h1 my-0">$67</h2>
            </SectionTriangle>
            <SectionTriangle id="" color="ff-cream" type="bottom">
              <p className="m-0">
                16 Masterclasses with experts from <br className="d-none d-sm-block" />
                the <i>Fantastic Fungi</i> community, including <br className="d-none d-sm-block" />
                written transcripts and downloadable MP3s
              </p>
              <hr className="border-color-black-50 w-25 my-4" />
              <p className="m-0">
                Extended interviews and deleted <br className="d-none d-sm-block" />
                scenes from <i>Fantastic Fungi</i>
              </p>
              <hr className="border-color-black-50 w-25 my-4" />
              <p className="m-0">
                A digital special event version <br className="d-none d-sm-block" />
                of the <i>Fantastic Fungi</i> film
              </p>
              <hr className="border-color-black-50 w-25 my-4" />
              <p className="m-0">A physical copy of our exclusive DVD</p>
              <hr className="border-color-black-50 w-25 my-4" />
              <p className="m-0">Bonus gifts worth ${bonusValue}</p>
            </SectionTriangle>
            <SectionTriangle id="" color="white">
              <ScreeningCheckoutButton className="mt-0 mb-4" />
              <SalesDisclaimer className="mt-0" />
            </SectionTriangle>
          </div>
        </SalesCopy>
      </div>

      <div className="footer-content mt-auto">
        <Footer partner />
        <ShareButtons floating />
      </div>
      <ModalExitScreening />
      <RegistrationModal />
    </div>
  );
}
